<div class="flow-container" *ngIf="organizationId; else noOrg">

  <!-- Stripe return loading overlay -->
  <div *ngIf="isHandlingStripeReturn" class="return-overlay">
    <mat-spinner diameter="48"></mat-spinner>
    <p>Processing Stripe return...</p>
  </div>

  <!-- Org Header -->
  <div class="org-header">
    <mat-icon class="org-icon">business</mat-icon>
    <div class="org-info">
      <span class="org-name">{{ organization?.Name || 'Organization' }}</span>
      <span class="org-id">ID: {{ organizationId }}</span>
    </div>
    <button mat-stroked-button (click)="startOver()" class="switch-btn">
      <mat-icon>swap_horiz</mat-icon>
      Switch Org
    </button>
  </div>

  <!-- Stepper -->
  <mat-stepper #stepper linear="false" class="onboarding-stepper" labelPosition="bottom">

    <!-- Step 1: Business Details -->
    <mat-step [completed]="step0Complete" label="Business Details">
      <ng-template matStepLabel>
        <span class="step-label">Business Details</span>
      </ng-template>
      <div class="step-content">
        <app-business-details
          [organizationId]="organizationId!"
          (stepComplete)="onBusinessDetailsComplete()"
        ></app-business-details>
      </div>
    </mat-step>

    <!-- Step 2: Branding -->
    <mat-step [completed]="step1Complete" label="Branding">
      <ng-template matStepLabel>
        <span class="step-label">Branding</span>
      </ng-template>
      <div class="step-content">
        <app-org-branding
          [organizationId]="organizationId!"
          (stepComplete)="onBrandingComplete()"
          (stepSkipped)="onBrandingSkipped()"
        ></app-org-branding>
      </div>
    </mat-step>

    <!-- Step 3: Representative -->
    <mat-step [completed]="step2Complete" label="Representative" [editable]="true">
      <ng-template matStepLabel>
        <span class="step-label">Representative</span>
      </ng-template>
      <div class="step-content">
        <app-representative-details
          [organizationId]="organizationId!"
          (stepComplete)="onRepresentativeDetailsComplete()"
        ></app-representative-details>
      </div>
    </mat-step>

    <!-- Step 4: Documents -->
    <mat-step [completed]="step3Complete" label="Documents">
      <ng-template matStepLabel>
        <span class="step-label">Documents</span>
      </ng-template>
      <div class="step-content">
        <app-document-upload
          [organizationId]="organizationId!"
          (stepComplete)="onDocumentsComplete()"
        ></app-document-upload>
      </div>
    </mat-step>

    <!-- Step 5: Stripe Connect -->
    <mat-step [completed]="step4Complete" label="Stripe Connect (Org Account)" [editable]="isAuthVerified">
      <ng-template matStepLabel>
        <span class="step-label">Stripe Connect</span>
      </ng-template>
      <div class="step-content">
        <div *ngIf="!isAuthVerified" class="auth-required-notice">
          <mat-icon>lock</mat-icon>
          <p><strong>Email verification required</strong></p>
          <p>This sensitive step requires you to verify your email address first.</p>
          <p>Check your email for a verification link, or ask your organization contact to resend it.</p>
          <button mat-raised-button color="primary" (click)="resendVerificationEmail()" [disabled]="resendingEmail" class="resend-btn">
            <mat-spinner *ngIf="resendingEmail" diameter="20" class="btn-spinner"></mat-spinner>
            <span>{{ resendingEmail ? 'Sending...' : 'Resend Verification Email' }}</span>
          </button>
        </div>
        <app-bank-integration
          *ngIf="isAuthVerified"
          [organizationId]="organizationId!"
          (stepComplete)="onStripeComplete()"
        ></app-bank-integration>
      </div>
    </mat-step>

    <!-- Step 6: Device Selection & Payment -->
    <mat-step [completed]="step5Complete" label="Donation Devices (Optional)" [editable]="isAuthVerified">
      <ng-template matStepLabel>
        <span class="step-label">Devices</span>
      </ng-template>
      <div class="step-content">
        <div *ngIf="!isAuthVerified" class="auth-required-notice">
          <mat-icon>lock</mat-icon>
          <p><strong>Email verification required</strong></p>
          <p>This step requires you to verify your email address first.</p>
          <p>Check your email for a verification link, or ask your organization contact to resend it.</p>
          <button mat-raised-button color="primary" (click)="resendVerificationEmail()" [disabled]="resendingEmail" class="resend-btn">
            <mat-spinner *ngIf="resendingEmail" diameter="20" class="btn-spinner"></mat-spinner>
            <span>{{ resendingEmail ? 'Sending...' : 'Resend Verification Email' }}</span>
          </button>
        </div>

        <!-- Device Selection Phase (show if step not complete, or if completing/adding more) -->
        <app-device-selection
          #deviceSelection
          *ngIf="isAuthVerified && (!step5Complete || !devicePaymentPhase)"
          [organizationId]="organizationId!"
          [hideNewOrderForm]="step5Complete && !devicePaymentPhase"
          (stepComplete)="onDeviceSelectionComplete($event)"
          (skipDevices)="onDeviceSkipped()"
        ></app-device-selection>

        <!-- Device Payment Phase -->
        <app-device-payment
          *ngIf="isAuthVerified && devicePaymentPhase"
          [organizationId]="organizationId!"
          [cart]="deviceCart"
          (paymentComplete)="onDevicePaymentComplete($event)"
          (paymentBack)="onDevicePaymentBack()"
        ></app-device-payment>
      </div>
    </mat-step>

    <!-- Step 7: Billing Setup (skipped if devices purchased with billing) -->
    <mat-step *ngIf="!skipBillingStep" [completed]="step6Complete" label="Billing Setup (Optional)" [editable]="isAuthVerified">
      <ng-template matStepLabel>
        <span class="step-label">Billing</span>
      </ng-template>
      <div class="step-content">
        <div *ngIf="!isAuthVerified" class="auth-required-notice">
          <mat-icon>lock</mat-icon>
          <p><strong>Email verification required</strong></p>
          <p>This step requires you to verify your email address first.</p>
          <p>Check your email for a verification link, or ask your organization contact to resend it.</p>
          <button mat-raised-button color="primary" (click)="resendVerificationEmail()" [disabled]="resendingEmail" class="resend-btn">
            <mat-spinner *ngIf="resendingEmail" diameter="20" class="btn-spinner"></mat-spinner>
            <span>{{ resendingEmail ? 'Sending...' : 'Resend Verification Email' }}</span>
          </button>
        </div>
        <app-billing-setup
          *ngIf="isAuthVerified"
          [organizationId]="organizationId!"
          (stepComplete)="onBillingSetupComplete()"
        ></app-billing-setup>
      </div>
    </mat-step>

    <!-- Step 8: Status & Brevo (final step) -->
    <mat-step [completed]="step7Complete" label="Email & Status" [editable]="isAuthVerified">
      <ng-template matStepLabel>
        <span class="step-label">Email & Status</span>
      </ng-template>
      <div class="step-content">
        <div *ngIf="!isAuthVerified" class="auth-required-notice">
          <mat-icon>lock</mat-icon>
          <p><strong>Email verification required</strong></p>
          <p>This step requires you to verify your email address first.</p>
          <p>Check your email for a verification link, or ask your organization contact to resend it.</p>
          <button mat-raised-button color="primary" (click)="resendVerificationEmail()" [disabled]="resendingEmail" class="resend-btn">
            <mat-spinner *ngIf="resendingEmail" diameter="20" class="btn-spinner"></mat-spinner>
            <span>{{ resendingEmail ? 'Sending...' : 'Resend Verification Email' }}</span>
          </button>
        </div>
        <app-status-dashboard
          *ngIf="isAuthVerified"
          [organizationId]="organizationId!"
          (onboardingComplete)="onOnboardingComplete()"
        ></app-status-dashboard>
      </div>
    </mat-step>

  </mat-stepper>
</div>

<ng-template #noOrg>
  <div class="no-org">
    <mat-icon>business</mat-icon>
    <p>Please search for or create an organization to begin onboarding.</p>
  </div>
</ng-template>
